import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { TextArea } from "@/components/ui/Input";
import { cn } from "@/lib/utils";

import { DeferredCommitFieldFrame } from "./DeferredCommitFieldFrame";
import {
  deferredCommitControlClassName,
  deferredCommitOverlayGlyphPadding,
  useDeferredCommitFieldFeedback,
} from "./deferredCommitFieldShared";
import {
  deferredCommitStatusGlyphVisible,
} from "./DeferredCommitStatusGlyph";

function normalizeDeferredCommitText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

export type DeferredCommitTextAreaHandle = {
  wrapSelection: (before: string, after: string) => void;
  insertAtCursor: (text: string) => void;
};

/** Multiline deferred commit field for table/header edits. */
export const DeferredCommitTextArea = forwardRef<
  DeferredCommitTextAreaHandle,
  {
  value: string;
  disabled: boolean;
  pending?: boolean;
  commitError?: string | null;
  commitSucceeded?: boolean;
  className?: string;
  placeholder?: string;
  label?: ReactNode;
  /** Grow past the minimum height so the current text is visible. */
  growWithContent?: boolean;
  onCommit: (value: string | null) => void;
}
>(function DeferredCommitTextArea({
  value,
  disabled,
  pending,
  commitError,
  commitSucceeded,
  className,
  placeholder,
  label,
  growWithContent = false,
  onCommit,
}, ref): JSX.Element {
  const [draft, setDraft] = useState(value);
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const skipNextBlurCommitRef = useRef(false);
  const writeDraft = useCallback(
    (next: string) => {
      setDraft(next);
      onCommit(normalizeDeferredCommitText(next));
    },
    [onCommit],
  );
  useImperativeHandle(
    ref,
    () => ({
      wrapSelection(before: string, after: string) {
        const current = draftRef.current;
        const area = areaRef.current;
        const start = area?.selectionStart ?? current.length;
        const end = area?.selectionEnd ?? start;
        writeDraft(current.slice(0, start) + before + current.slice(start, end) + after + current.slice(end));
      },
      insertAtCursor(text: string) {
        const current = draftRef.current;
        const area = areaRef.current;
        const start = area?.selectionStart ?? current.length;
        const end = area?.selectionEnd ?? start;
        writeDraft(current.slice(0, start) + text + current.slice(end));
      },
    }),
    [writeDraft],
  );
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const isDirty = useMemo(() => {
    return normalizeDeferredCommitText(draft) !== normalizeDeferredCommitText(value);
  }, [draft, value]);

  const flashTone = useDeferredCommitFieldFeedback(commitSucceeded, commitError);
  const showOverlayGlyph = deferredCommitStatusGlyphVisible({
    pending,
    isDirty,
    commitError,
  });

  useLayoutEffect(() => {
    const area = areaRef.current;
    if (!growWithContent || area == null) return;
    area.style.height = "auto";
    area.style.height = `${area.scrollHeight}px`;
  }, [draft, growWithContent]);

  const commitDraft = useCallback((): void => {
    if (pending === true) {
      return;
    }
    const next = normalizeDeferredCommitText(draft);
    const current = normalizeDeferredCommitText(value);
    if (next !== current) {
      onCommit(next);
    }
  }, [draft, onCommit, pending, value]);

  return (
    <DeferredCommitFieldFrame
      label={label}
      pending={pending}
      isDirty={isDirty}
      disabled={disabled}
      commitError={commitError}
      commitSucceeded={commitSucceeded}
      showOverlayGlyph={showOverlayGlyph}
    >
      <TextArea
        ref={areaRef}
        value={draft}
        disabled={disabled}
        placeholder={placeholder}
        aria-busy={pending === true}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={() => {
          if (skipNextBlurCommitRef.current) {
            skipNextBlurCommitRef.current = false;
            return;
          }
          commitDraft();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            skipNextBlurCommitRef.current = true;
            commitDraft();
            event.currentTarget.blur();
          }
        }}
        className={cn(
          "min-w-0 w-full min-h-8 resize-y overflow-auto py-1.5 font-sans text-xs",
          deferredCommitOverlayGlyphPadding(showOverlayGlyph && !label),
          className,
          deferredCommitControlClassName({
            isDirty,
            disabled,
            pending,
            commitError,
            flashTone,
          }),
        )}
      />
    </DeferredCommitFieldFrame>
  );
});
DeferredCommitTextArea.displayName = "DeferredCommitTextArea";
